import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { annonceService, sousServiceService, authService } from '../../api';
import './AjoutAnnonceUser.css';
import Header from '../Header/Header';
import Footer from '../footer/Footer';

const AjoutAnnonceUser = () => {
  const navigate = useNavigate();
  const [annonce, setAnnonce] = useState({
    titre: '',
    description: '',
    type_annonce: 'offre',
    prix: '',
    ville: '',
    sous_service_id: '',
    image: null
  });
  const [sousServices, setSousServices] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({
    titre: false,
    description: false,
    prix: false,
    ville: false,
    sous_service_id: false,
    image: false
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [villeSuggestions, setVilleSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Vérifier si les données utilisateur sont déjà en localStorage
          let userData = JSON.parse(localStorage.getItem('user') || '{}');
          
          // Si pas de données utilisateur, récupérer le profil
          if (!userData || !userData.id) {
            const profileResponse = await authService.getProfile();
            userData = profileResponse.data || profileResponse;
            localStorage.setItem('user', JSON.stringify(userData));
          }
          
          setIsAuthenticated(true);
          
          // Charger les sous-services
          const sousServicesResponse = await sousServiceService.getAll();
          setSousServices(sousServicesResponse.data || sousServicesResponse);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'authentification:', error);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  const searchVilles = async (query) => {
    if (query.length < 2) {
      setVilleSuggestions([]);
      return;
    }

    try {
      const response = await axios.get(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&type=municipality&limit=5`
      );
      
      const villes = response.data.features.map(feature => ({
        name: feature.properties.city,
        postalCode: feature.properties.postcode
      }));
      
      const uniqueVilles = [...new Map(villes.map(item => [item.name, item])).values()];
      setVilleSuggestions(uniqueVilles);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Erreur lors de la recherche des villes:", error);
    }
  };

  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'titre':
        if (!value.trim()) error = 'Le titre est requis';
        else if (value.trim().length < 5) error = 'Le titre doit contenir au moins 5 caractères';
        break;
      case 'description':
        if (!value.trim()) error = 'La description est requise';
        else if (value.trim().length < 20) error = 'La description doit contenir au moins 20 caractères';
        break;
      case 'prix':
        if (!value) error = 'Le prix est requis';
        else if (isNaN(value)) error = 'Le prix doit être un nombre';
        else if (parseFloat(value) < 0) error = 'Le prix doit être supérieur à 0';
        break;
      case 'ville':
        if (!value.trim()) error = 'La ville est requise';
        break;
      case 'sous_service_id':
        if (!value) error = 'Le sous-service est requis';
        break;
      case 'image':
        if (value) {
          const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
          if (!validTypes.includes(value.type)) {
            error = 'Le fichier doit être une image (JPEG, PNG, JPG ou GIF)';
          } else if (value.size > 2 * 1024 * 1024) {
            error = 'La taille de l\'image ne doit pas dépasser 2MB';
          }
        }
        break;
      default:
        break;
    }
    
    return error;
  };

  useEffect(() => {
    const newErrors = {};
    Object.keys(touched).forEach(field => {
      if (touched[field]) {
        const error = validateField(field, annonce[field]);
        if (error) {
          newErrors[field] = error;
        }
      }
    });
    setErrors(newErrors);
  }, [annonce, touched]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAnnonce(prev => ({ ...prev, [name]: value }));
    
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error || undefined
      }));
    }
  };

  const handleVilleChange = (e) => {
    const value = e.target.value;
    setAnnonce(prev => ({ ...prev, ville: value }));
    searchVilles(value);
    
    if (touched.ville) {
      const error = validateField('ville', value);
      setErrors(prev => ({ ...prev, ville: error || undefined }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    
    const error = validateField(name, annonce[name]);
    setErrors(prev => ({
      ...prev,
      [name]: error || undefined
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAnnonce(prev => ({ ...prev, image: file }));
      setTouched(prev => ({ ...prev, image: true }));
      
      const error = validateField('image', file);
      setErrors(prev => ({
        ...prev,
        image: error || undefined
      }));
    } else {
      setAnnonce(prev => ({ ...prev, image: null }));
      setErrors(prev => ({
        ...prev,
        image: undefined
      }));
    }
  };

  const selectVille = (ville) => {
    setAnnonce(prev => ({ ...prev, ville: ville.name }));
    setVilleSuggestions([]);
    setShowSuggestions(false);
    setTouched(prev => ({ ...prev, ville: true }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    Object.keys(annonce).forEach(field => {
      if (field === 'image') return;
      
      const error = validateField(field, annonce[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });
    
    setErrors(newErrors);
    setTouched({
      titre: true,
      description: true,
      prix: true,
      ville: true,
      sous_service_id: true,
      image: true
    });
    
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('titre', annonce.titre);
      formData.append('description', annonce.description);
      formData.append('type_annonce', annonce.type_annonce);
      formData.append('prix', annonce.prix);
      formData.append('ville', annonce.ville);
      formData.append('sous_service_id', annonce.sous_service_id);
      
      // Récupérer l'ID utilisateur depuis localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('Données utilisateur récupérées:', userData);
      
      if (userData && userData.id) {
        formData.append('user_id', userData.id);
        console.log('Ajout de user_id:', userData.id);
      } else {
        // Si pas d'ID, tenter de récupérer le profil
        try {
          const profileResponse = await authService.getProfile();
          const freshUserData = profileResponse.data || profileResponse;
          if (freshUserData && freshUserData.id) {
            formData.append('user_id', freshUserData.id);
            console.log('Ajout de user_id (après récupération):', freshUserData.id);
          } else {
            throw new Error('ID utilisateur non disponible');
          }
        } catch (profileError) {
          throw new Error('Impossible de récupérer l\'ID utilisateur. Veuillez vous reconnecter.');
        }
      }
      
      if (annonce.image) {
        formData.append('image', annonce.image);
      }

      console.log('FormData envoyé:');
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }
      
      const response = await annonceService.create(formData);
      console.log('Réponse de création:', response);
      
      navigate('/annonces', { state: { success: 'Annonce créée avec succès' } });
    } catch (error) {
      console.error('Erreur détaillée:', error);
      setErrors({
        submit: error.response?.data?.message || error.message || 'Erreur lors de la création de l\'annonce'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="ajout-annonce-container">
        <Header />
        <div className="ajout-annonce-wrapper">
          <div className="ajout-annonce-content">
            <div className="annonce-form-header">
              <h1>Accès non autorisé</h1>
              <p>Vous devez être connecté pour déposer une annonce</p>
            </div>
            <div className="not-connected-message">
              <p>Veuillez vous connecter ou créer un compte pour accéder à cette fonctionnalité.</p>
              <button 
                className="submit-button" 
                onClick={() => navigate('/connexion')}
                style={{ marginTop: '1rem' }}
              >
                Se connecter
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="ajout-annonce-container">
      <Header />
      
      <div className="ajout-annonce-wrapper">
        <div className="ajout-annonce-content">
          <div className="annonce-form-header">
            <h1>Déposer une annonce</h1>
            <p>Remplissez le formulaire pour créer votre annonce</p>
          </div>

          <form onSubmit={handleSubmit} className="annonce-form" encType="multipart/form-data">
            {errors.submit && <div className="form-error-message">{errors.submit}</div>}
            
            <div className="form-group">
              <label htmlFor="titre">Titre *</label>
              <input
                id="titre"
                type="text"
                name="titre"
                value={annonce.titre}
                onChange={handleChange}
                onBlur={handleBlur}
                className={errors.titre ? 'input-error' : ''}
                placeholder="Titre de votre annonce"
              />
              {errors.titre && <span className="error-message">{errors.titre}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                name="description"
                value={annonce.description}
                onChange={handleChange}
                onBlur={handleBlur}
                rows="5"
                className={errors.description ? 'input-error' : ''}
                placeholder="Décrivez en détail ce que vous proposez ou recherchez"
              ></textarea>
              {errors.description && <span className="error-message">{errors.description}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="type_annonce">Type d'annonce *</label>
                <select
                  id="type_annonce"
                  name="type_annonce"
                  value={annonce.type_annonce}
                  onChange={handleChange}
                >
                  <option value="offre">Je propose un service</option>
                  <option value="demande">Je recherche un service</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="prix">Prix (€) *</label>
                <input
                  id="prix"
                  type="text"
                  name="prix"
                  value={annonce.prix}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.prix ? 'input-error' : ''}
                  placeholder="0.00"
                  min="0"
                  step="0.01"

                />
                {errors.prix && <span className="error-message">{errors.prix}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group ville-autocomplete-container">
                <label htmlFor="ville">Ville *</label>
                <div className="ville-autocomplete">
                  <input
                    id="ville"
                    type="text"
                    name="ville"
                    value={annonce.ville}
                    onChange={handleVilleChange}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    onFocus={() => annonce.ville.length >= 2 && setShowSuggestions(true)}
                    className={errors.ville ? 'input-error' : ''}
                    placeholder="Où se situe votre annonce ?"
                    autoComplete="off"
                  />
                  {errors.ville && <span className="error-message">{errors.ville}</span>}
                  
                  {showSuggestions && villeSuggestions.length > 0 && (
                    <ul className="ville-suggestions">
                      {villeSuggestions.map((ville, index) => (
                        <li 
                          key={index} 
                          onClick={() => selectVille(ville)}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {ville.name} ({ville.postalCode})
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="sous_service_id">Catégorie *</label>
                <select
                  id="sous_service_id"
                  name="sous_service_id"
                  value={annonce.sous_service_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={errors.sous_service_id ? 'input-error' : ''}
                >
                  <option value="">Sélectionnez une catégorie</option>
                  {sousServices.map(service => (
                    <option key={service.id} value={service.id}>
                      {service.nom}
                    </option>
                  ))}
                </select>
                {errors.sous_service_id && <span className="error-message">{errors.sous_service_id}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="image">Photo (facultative)</label>
              <div className="file-upload-container">
                <label className="file-upload-label">
                  <input
                    id="image"
                    type="file"
                    name="image"
                    onChange={handleFileChange}
                    accept="image/jpeg, image/png, image/jpg, image/gif"
                    className="file-input"
                  />
                  <span className="file-upload-text">
                    {annonce.image ? annonce.image.name : 'Choisir une image'}
                  </span>
                  <span className="file-upload-button">Parcourir</span>
                </label>
              </div>
              {errors.image && <span className="error-message">{errors.image}</span>}
              <p className="file-hint">Formats acceptés: JPG, PNG, JPEG, GIF (max 2MB)</p>
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? 'Publication en cours...' : 'Publier mon annonce'}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default AjoutAnnonceUser;